import { Component, OnInit, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService, User, UserRole, normaliseRole } from '../auth.service';
import { LogoutButtonComponent } from '../logout-button/logout-button.component';
import { Router, RouterModule } from '@angular/router';
import { AUTH_CONSTANTS } from '../../shared/constants';
import { PermissionService } from '../../shared/services/permission.service';

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [CommonModule, LogoutButtonComponent, RouterModule],
  template: `
    <div class="user-profile" *ngIf="user">

      <!-- ── Desktop: pill trigger ── -->
      <div class="profile-pill" (click)="toggleDropdown($event)" [class.open]="isOpen" role="button" tabindex="0"
           (keydown.enter)="toggleDropdown($event)" (keydown.space)="toggleDropdown($event)">
        <div class="pill-avatar">{{ getInitials() }}</div>
        <span class="pill-name">{{ user.name }}</span>
        <span class="role-badge" [ngClass]="getRoleBadgeClass()">{{ getRoleDisplayName() }}</span>
        <svg class="pill-chevron" [class.rotated]="isOpen" width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M3 5l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      <!-- ── Desktop: dropdown panel ── -->
      <div class="dropdown-panel" [class.open]="isOpen" (click)="$event.stopPropagation()">
        <div class="dropdown-header">
          <div class="dh-avatar">{{ getInitials() }}</div>
          <div class="dh-meta">
            <div class="dh-name">{{ user.name }}</div>
            <div class="dh-email">{{ user.email }}</div>
            <div class="dh-company" *ngIf="user.company">{{ user.company }}</div>
          </div>
        </div>
        <div class="dh-role-row">
          <span class="role-badge" [ngClass]="getRoleBadgeClass()">{{ getRoleDisplayName() }}</span>
        </div>
        <div class="dropdown-divider"></div>
        <div class="dropdown-actions">
          <button *ngIf="canAccessAdminRoutes()" class="btn-admin" [class.admin-active]="isOnAdminRoute()" (click)="navigateToAdmin()">Admin</button>
          <app-logout-button></app-logout-button>
        </div>
      </div>

      <!-- ── Mobile: flat layout (always visible in hamburger panel) ── -->
      <div class="mobile-profile">
        <div class="mobile-top">
          <div class="pill-avatar">{{ getInitials() }}</div>
          <div class="mobile-meta">
            <div class="dh-name">{{ user.name }}</div>
            <div class="dh-email">{{ user.email }}</div>
            <div class="dh-company" *ngIf="user.company">{{ user.company }}</div>
          </div>
        </div>
        <div class="mobile-role-row">
          <span class="role-badge" [ngClass]="getRoleBadgeClass()">{{ getRoleDisplayName() }}</span>
        </div>
        <div class="mobile-actions">
          <button *ngIf="canAccessAdminRoutes()" class="btn-admin mobile-btn" [class.admin-active]="isOnAdminRoute()" (click)="navigateToAdmin()">Admin</button>
          <app-logout-button class="mobile-btn"></app-logout-button>
        </div>
      </div>

    </div>
  `,
  styles: [`
    /* ── Root wrapper ─────────────────────────────────── */
    .user-profile {
      position: relative;
    }

    /* ── Pill ─────────────────────────────────────────── */
    .profile-pill {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 10px 4px 5px;
      border: 1px solid var(--ap-border, #E3E6EB);
      border-radius: 99px;
      background: var(--ap-surface, #fff);
      cursor: pointer;
      user-select: none;
      transition: border-color 0.15s, box-shadow 0.15s;
      outline: none;
    }

    .profile-pill:hover,
    .profile-pill:focus-visible,
    .profile-pill.open {
      border-color: var(--ap-primary, #2F5BDA);
      box-shadow: 0 0 0 3px rgba(47,91,218,.10);
    }

    .pill-avatar {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
      font-size: 11px;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

    .pill-name {
      font-size: 13px;
      font-weight: 600;
      color: var(--ap-text, #0B1220);
      white-space: nowrap;
    }

    .pill-chevron {
      color: var(--ap-text-muted, #6B7280);
      flex-shrink: 0;
      transition: transform 0.2s ease;
      margin-left: 2px;
    }

    .pill-chevron.rotated { transform: rotate(180deg); }

    /* ── Role badge (shared pill + dropdown) ─────────── */
    .role-badge {
      display: inline-flex;
      align-items: center;
      font-size: 10px;
      font-weight: 700;
      padding: 3px 9px;
      border-radius: 99px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .role-badge.badge-super-admin { background: var(--ap-danger-soft, #FDECEA);   color: var(--ap-danger, #B42318); }
    .role-badge.badge-admin       { background: var(--ap-warn-soft, #FDF1DF);     color: var(--ap-warn, #B45309); }
    .role-badge.badge-user        { background: var(--ap-success-soft, #E3F4EC);  color: var(--ap-success, #047A55); }
    .role-badge.badge-god         { background: #F3E8FF; color: #7C3AED; border: 1px solid #DDD6FE; }
    .role-badge.badge-default     { background: var(--ap-surface-muted, #EEF0F3); color: var(--ap-text-muted, #6B7280); }

    /* ── Dropdown panel ───────────────────────────────── */
    .dropdown-panel {
      position: absolute;
      top: calc(100% + 8px);
      right: 0;
      width: 240px;
      background: var(--ap-surface, #fff);
      border: 1px solid var(--ap-border, #E3E6EB);
      border-radius: var(--ap-radius, 10px);
      box-shadow: 0 8px 28px rgba(11,18,32,0.13);
      z-index: 1100;
      opacity: 0;
      transform: translateY(-6px) scale(0.98);
      pointer-events: none;
      transition: opacity 0.16s ease, transform 0.16s ease;
    }

    .dropdown-panel.open {
      opacity: 1;
      transform: translateY(0) scale(1);
      pointer-events: auto;
    }

    .dropdown-header {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 14px 14px 8px;
    }

    .dh-avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
      font-size: 13px;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      text-transform: uppercase;
    }

    .dh-meta {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
      overflow: hidden;
    }

    .dh-name {
      font-size: 13px;
      font-weight: 600;
      color: var(--ap-text, #0B1220);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .dh-email {
      font-size: 11px;
      color: var(--ap-text-muted, #6B7280);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .dh-company {
      font-size: 11px;
      color: var(--ap-primary, #2F5BDA);
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .dh-role-row {
      padding: 4px 14px 10px;
    }

    .dropdown-divider {
      height: 1px;
      background: var(--ap-border, #E3E6EB);
    }

    /* ── Dropdown buttons ─────────────────────────────── */
    .dropdown-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 14px 12px;
    }

    .btn-admin {
      height: 30px;
      padding: 0 13px;
      background: var(--ap-primary-soft, #E8EEFC);
      color: var(--ap-primary, #2F5BDA);
      border: none;
      border-radius: var(--ap-radius-sm, 6px);
      font-size: 12px;
      font-weight: 550;
      cursor: pointer;
      transition: background 0.15s, color 0.15s;
      white-space: nowrap;
    }

    .btn-admin:hover {
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
    }

    .btn-admin.admin-active {
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
    }

    .btn-admin.admin-active:hover {
      background: var(--ap-primary-ink, #1E3FA8);
    }

    /* ── Mobile layout ────────────────────────────────── */
    .mobile-profile { display: none; }

    @media (max-width: 768px) {
      .profile-pill,
      .dropdown-panel {
        display: none !important;
      }

      .mobile-profile {
        display: flex;
        flex-direction: column;
        gap: 0;
        width: 100%;
      }

      .mobile-top {
        display: flex;
        align-items: center;
        gap: 12px;
        padding-bottom: 10px;
      }

      .mobile-profile .pill-avatar {
        width: 44px;
        height: 44px;
        font-size: 15px;
        flex-shrink: 0;
      }

      .mobile-meta {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
        overflow: hidden;
      }

      .mobile-meta .dh-name {
        font-size: 15px;
        font-weight: 600;
        color: var(--ap-text, #0B1220);
      }

      .mobile-meta .dh-email {
        font-size: 12px;
        color: var(--ap-text-muted, #6B7280);
      }

      .mobile-meta .dh-company {
        font-size: 12px;
        color: var(--ap-primary, #2F5BDA);
        font-weight: 500;
      }

      .mobile-role-row {
        padding-bottom: 12px;
      }

      /* Equal-width horizontal buttons */
      .mobile-actions {
        display: flex;
        flex-direction: row;
        gap: 8px;
        width: 100%;
      }

      .mobile-btn {
        flex: 1;
        height: 38px;
        font-size: 13px;
        border-radius: var(--ap-radius-sm, 6px);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        min-width: 0;
      }

      .mobile-profile .btn-admin.mobile-btn {
        height: 38px;
        padding: 0;
      }
    }
  `]
})
export class UserProfileComponent implements OnInit {
  user: User | null = null;
  isAdmin: boolean = false;
  isOpen = false;

  constructor(
    private authService: AuthService,
    private permissionService: PermissionService,
    private router: Router,
    private elRef: ElementRef
  ) {}

  ngOnInit(): void {
    const storedUser = localStorage.getItem(AUTH_CONSTANTS.USER_STORAGE_KEY);
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        this.user = parsedUser;
        this.isAdmin = UserRole.ADMIN === normaliseRole(parsedUser.role);
      } catch (e) {
        console.error('Error parsing stored user:', e);
      }
    }

    this.authService.currentUser$.subscribe(user => {
      this.user = user;
      if (user) {
        this.isAdmin = this.permissionService.canAccessAdminRoutes(user.role);
      } else {
        this.isAdmin = false;
      }
    });
  }

  toggleDropdown(event: Event) {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.isOpen = false;
  }

  getInitials(): string {
    const parts = (this.user?.name || '').trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
  }

  getRoleDisplayName(): string {
    return this.permissionService.getRoleDisplayName(this.user?.role);
  }

  getRoleBadgeClass(): string {
    return this.permissionService.getRoleBadgeClass(this.user?.role);
  }

  canAccessAdminRoutes(): boolean {
    return this.permissionService.canAccessAdminRoutes(this.user?.role);
  }

  isOnAdminRoute(): boolean {
    const url = this.router.url;
    return url.startsWith('/admin') || url.startsWith('/god');
  }

  navigateToAdmin(): void {
    this.isOpen = false;
    if (this.user?.role === UserRole.PLATFORM_OWNER) {
      this.router.navigate(['/god/overview']);
    } else if (this.user?.role === UserRole.OWNER) {
      this.router.navigate(['/admin/companies']);
    } else if (this.user?.company_type === 'MC') {
      this.router.navigate(['/admin/users']);
    } else {
      this.router.navigate(['/admin']);
    }
  }
}
